import { compressImageFile } from "@bto/shared";
import React, { useEffect, useState } from "react";
import Dropzone, {
  IFileWithMeta,
  IPreviewProps,
  StatusValue,
} from "react-dropzone-uploader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashCanXmark,
  faMemoCircleCheck,
} from "@fortawesome/pro-regular-svg-icons";
import { createTheme, LinearProgress, ThemeProvider } from "@mui/material";
import { useRecoilValue } from "recoil";
import { useParams } from "react-router-dom";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  FileUploaderBrowse,
  FileUploaderDropzoneWrapper,
  FileUploaderWrapper,
  FileUploaderPreview,
  FileUploaderPreviewTitle,
  FileUploaderPreviewDelete,
  FileUploaderPreviewError,
  FileNameUploaded,
  InsuranceFileUploaderGreyText,
  InsuranceFileUploaderDropzoneAntigen,
  FileUploaderTitle,
  InsuranceFileUploaderMobile,
  InsuranceFileUploaderHelpText,
} from "./InsuranceFileUploader.styles";
import "react-dropzone-uploader/dist/styles.css";

import { themePalette } from "../../theme";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import { getUserAuth } from "../../recoil/selectors";
import { uploadInsuranceImageForProcessing } from "./Services/InsuranceUploadService";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { UploadInsuranceCardForBill } from "../UpfrontPayment/Services/UpfrontPaymentService";
import { toBase64 } from "../../utils/helpers";
import { BillingSupportNumber } from "../UpfrontPayment/types";
import { Banner } from "../Banner/Banner";
import { InsuranceUploadType } from "./types";

type FileData = {
  s3Url: string;
  id: string;
  valid: boolean;
  file: string;
};

export interface FileUploadInfo {
  front?: FileData;
  back?: FileData;
}

const InsuranceFileUploadFeedback = (t: TFunction, side?: "front" | "back") => {
  if (side) {
    return (
      <InsuranceFileUploaderMobile>
        {t(`insuranceUpload.fileUpload.addImage.${side}`)}
      </InsuranceFileUploaderMobile>
    );
  }
  return (
    <InsuranceFileUploaderDropzoneAntigen key="custom">
      <FileUploaderBrowse>
        {t("insuranceUpload.fileUpload.chooseFile")}
      </FileUploaderBrowse>
      <InsuranceFileUploaderHelpText>
        <InsuranceFileUploaderGreyText>
          <b>{t("insuranceUpload.fileUpload.dragAndDrop")}</b>
        </InsuranceFileUploaderGreyText>
        <InsuranceFileUploaderGreyText fixedWidth>
          {t("insuranceUpload.fileUpload.supportedTypes")}
        </InsuranceFileUploaderGreyText>
      </InsuranceFileUploaderHelpText>
    </InsuranceFileUploaderDropzoneAntigen>
  );
};

const MAX_FILE_SIZE_BYTES = 10_000_000; // 10MB — matches backend FileBase64Field.MAX_FILE_SIZE_MB

interface FileUploaderProps {
  setValue: (files: FileUploadInfo) => void;
  insuranceType: InsuranceUploadType;
}

const InsuranceFileUploader: React.FC<FileUploaderProps> = ({
  setValue,
  insuranceType,
}) => {
  const { t } = useTranslation();
  const [dropzoneKey] = useState(0);
  const [activeFiles, setActiveFiles] = useState<FileUploadInfo>({});
  const [uploadProcessing, setUploadProcessing] = useState(false);
  const [error, setError] = useState("");
  const { deviceType } = useDeviceHook();
  const authInfo = useRecoilValue(getUserAuth);
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();

  const PreviewComponent = ({ meta, fileWithMeta }: IPreviewProps) => {
    const { name, id } = meta;
    const { remove } = fileWithMeta;

    // Find the active file by matching the meta id
    const activeFile = Object.values(activeFiles).find(
      (file) => file?.id === id,
    );

    const theme = createTheme({
      palette: {
        primary: {
          main: "#017E72",
        },
      },
    });

    return (
      <FileUploaderPreview validUpload={activeFile?.valid}>
        <FileUploaderPreviewTitle>
          {activeFile?.valid !== undefined &&
            (activeFile?.valid ? (
              <FontAwesomeIcon
                icon={faMemoCircleCheck}
                size="2x"
                color="#017E72"
              />
            ) : (
              <ExclamationIcon color={themePalette.redMedium} />
            ))}
          <FileNameUploaded>
            <ThemeProvider theme={theme}>
              {name}
              {activeFile?.valid === undefined && (
                <LinearProgress color="primary" />
              )}
            </ThemeProvider>
          </FileNameUploaded>
        </FileUploaderPreviewTitle>
        <FileUploaderPreviewDelete>
          <FileUploaderPreviewError>
            {activeFile?.valid === false
              ? t("insuranceUpload.fileUpload.uploadFailed")
              : ""}
          </FileUploaderPreviewError>
          {activeFile?.valid !== undefined && (
            <button
              onClick={() => remove()}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
              type="button"
            >
              <FontAwesomeIcon
                icon={faTrashCanXmark}
                size="2x"
                color={themePalette.redMedium}
              />
            </button>
          )}
        </FileUploaderPreviewDelete>
      </FileUploaderPreview>
    );
  };

  useEffect(() => {
    setValue(activeFiles);
  }, [activeFiles]);

  const handleChangeStatus: (
    recentFile: IFileWithMeta,
    status: StatusValue,
    side: "back" | "front",
  ) => void = async (
    recentFile: IFileWithMeta,
    status: StatusValue,
    side: "back" | "front",
  ) => {
    const fileId = recentFile.meta.id;
    const isOnBillingPage = Boolean(billingPK);
    const hasValidCredential = isOnBillingPage
      ? billingPK
      : authInfo?.access_token;

    if (status === "error_file_size") {
      setError(t("insuranceUpload.fileUpload.errors.fileTooLarge"));
      recentFile.remove();
      return;
    }

    if (status === "rejected_file_type") {
      setError(t("insuranceUpload.fileUpload.errors.unsupportedType"));
      recentFile.remove();
      return;
    }

    if (status === "done" && hasValidCredential) {
      setError("");
      setUploadProcessing(true);

      let response: Response;
      try {
        const compressedFile = await compressImageFile(recentFile.file);
        const base64Image = await toBase64(compressedFile);

        response = isOnBillingPage
          ? await UploadInsuranceCardForBill(
              base64Image,
              billingPK,
              insuranceType,
              adminID,
            )
          : await uploadInsuranceImageForProcessing(
              base64Image,
              authInfo!.access_token,
            );

        setUploadProcessing(false);

        const responseData = await response.json().catch(() => null);

        if (response.ok && responseData?.s3_url) {
          setActiveFiles((active) => ({
            ...active,
            [side]: {
              s3Url: responseData.s3_url,
              valid: true,
              id: fileId,
              file: base64Image,
            },
          }));
        } else if (response.ok) {
          console.error("Insurance card upload: missing s3_url in response", {
            side,
            responseData,
          });
          setError(
            t("insuranceUpload.fileUpload.errors.uploadFailed", {
              phone: BillingSupportNumber,
            }),
          );
          recentFile.remove();
        } else if (response.status === 422) {
          setError(
            t("insuranceUpload.fileUpload.errors.unableToUpload", {
              phone: BillingSupportNumber,
            }),
          );
          recentFile.remove();
        } else {
          const apiError =
            responseData?.file_data?.[0] ||
            responseData?.error ||
            t("insuranceUpload.fileUpload.errors.uploadFailed", {
              phone: BillingSupportNumber,
            });
          console.error("Insurance card upload failed", {
            status: response.status,
            error: apiError,
            side,
          });
          setError(apiError);
          recentFile.remove();
        }
      } catch (err) {
        console.error("Insurance card upload error", { side, err });
        setError(
          t("insuranceUpload.fileUpload.errors.uploadFailed", {
            phone: BillingSupportNumber,
          }),
        );
        recentFile.remove();
        setUploadProcessing(false);
      }
    } else if (status === "removed") {
      const newActiveFiles = { ...activeFiles };

      if (newActiveFiles.front?.id === fileId) {
        delete newActiveFiles.front;
      } else if (newActiveFiles.back?.id === fileId) {
        delete newActiveFiles.back;
      }

      setActiveFiles(newActiveFiles);
    }
  };

  return (
    <>
      {error && (
        <Banner
          bannerType="warning"
          bannerTitle={t("insuranceUpload.errors.title")}
          bannerText={error}
        />
      )}

      <FileUploaderWrapper>
        <FileUploaderTitle>
          {t("insuranceUpload.fileUpload.frontOfCard", {
            cardType: t(
              `insuranceUpload.fileUpload.cardTypes.${
                insuranceType === InsuranceUploadType.medicaid_or_hmo
                  ? "medicaid"
                  : "insurance"
              }`,
            ),
          })}
        </FileUploaderTitle>
        <FileUploaderDropzoneWrapper deviceType={deviceType}>
          <Dropzone
            disabled={uploadProcessing}
            key={dropzoneKey}
            accept=".png, .jpg, .jpeg, .pdf"
            maxSizeBytes={MAX_FILE_SIZE_BYTES}
            onChangeStatus={(recentFile: IFileWithMeta, status: StatusValue) =>
              handleChangeStatus(recentFile, status, "front")
            }
            maxFiles={1}
            submitButtonDisabled
            styles={{
              dropzone: {
                border: "none",
                height: "145px",
                overflow: "hidden",
                background: themePalette.neutral50,
                display: "flex",
                flexFlow: "wrap",
                flexDirection: "row",
                overflowY: "scroll",
                alignItems: "start",
                justifyContent: "center",
              },
            }}
            inputContent={InsuranceFileUploadFeedback(
              t,
              deviceType === "mobile" ? "front" : undefined,
            )}
            PreviewComponent={PreviewComponent}
          />
        </FileUploaderDropzoneWrapper>
        <FileUploaderTitle>
          {t("insuranceUpload.fileUpload.backOfCard", {
            cardType: t(
              `insuranceUpload.fileUpload.cardTypes.${
                insuranceType === InsuranceUploadType.medicaid_or_hmo
                  ? "medicaid"
                  : "insurance"
              }`,
            ),
          })}
        </FileUploaderTitle>
        <FileUploaderDropzoneWrapper deviceType={deviceType}>
          <Dropzone
            disabled={uploadProcessing}
            key={dropzoneKey}
            accept=".png, .jpg, .jpeg, .pdf"
            maxSizeBytes={MAX_FILE_SIZE_BYTES}
            onChangeStatus={(recentFile: IFileWithMeta, status: StatusValue) =>
              handleChangeStatus(recentFile, status, "back")
            }
            maxFiles={1}
            submitButtonDisabled
            styles={{
              dropzone: {
                border: "none",
                height: "145px",
                overflow: "hidden",
                background: themePalette.neutral50,
                display: "flex",
                flexFlow: "wrap",
                flexDirection: "row",
                overflowY: "scroll",
                alignItems: "start",
                justifyContent: "center",
              },
            }}
            inputContent={InsuranceFileUploadFeedback(
              t,
              deviceType === "mobile" ? "back" : undefined,
            )}
            PreviewComponent={PreviewComponent}
          />
        </FileUploaderDropzoneWrapper>
      </FileUploaderWrapper>
    </>
  );
};

export default InsuranceFileUploader;
